import axios from "axios";
import { API } from "./backend";
import { installStagingInterceptors } from "./stagingGate";
import { installWriteGenerations } from "./sharedRead";

export { API };

const TOKEN_KEY = "vat_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const authClient = axios.create({ baseURL: API, timeout: 30000 });
installStagingInterceptors(authClient);
// A1-95 — review acks, VIES overrides, freeze, sign-off and settings go
// through this client; each must start a new read generation.
installWriteGenerations(authClient);

authClient.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const login = async (email, password) => {
  const { data } = await authClient.post("/auth/login", { email, password });
  setToken(data.access_token);
  return data;
};

export const me = async () => {
  const { data } = await authClient.get("/auth/me");
  return data;
};

export const changePassword = async (current_password, new_password) => {
  const { data } = await authClient.post("/auth/change-password", {
    current_password, new_password,
  });
  // v0.6.8 fix — the backend now revokes every token issued before
  // this password change (closing a "stolen token survives a password
  // reset" gap) and mints a fresh one in the response so THIS session
  // keeps working. Without storing it here, the old token still in
  // localStorage would be rejected on the very next request.
  if (data?.access_token) setToken(data.access_token);
  return data;
};

export const logout = async () => {
  // v0.6.8 fix — this used to only clear the local token; the backend
  // endpoint existed but was never called, so a leaked/stolen token
  // stayed valid server-side for up to its full 24h lifetime even
  // after the legitimate user explicitly signed out. Best-effort: a
  // network failure here must still clear the local token so the user
  // isn't stuck unable to sign out.
  try {
    await authClient.post("/auth/logout");
  } catch {
    // ignored — local sign-out proceeds regardless.
  } finally {
    clearToken();
  }
};

export const getSettings = async () => (await authClient.get("/settings")).data;
export const patchSettings = async (patch) => (await authClient.patch("/settings", patch)).data;
// v0.6.9 — Settings > Exchange Rates preset.
export const listFxRatePresets = async () => (await authClient.get("/settings/fx-rates")).data;
export const uploadFxRatePreset = async (file) => {
  const form = new FormData();
  form.append("file", file);
  return (await authClient.post("/settings/fx-rates/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  })).data;
};
export const deleteFxRatePreset = async (id) => (await authClient.delete(`/settings/fx-rates/${id}`)).data;
// v0.6.9 Phase 2 — Settings > VAT Rates manual override.
export const listVatRateOverrides = async () => (await authClient.get("/settings/vat-rates")).data;
// Roadmap 2.3 — law-change watch list: checks due now, changes to assess.
export const getRulesWatchSummary = async () => (await authClient.get("/settings/rules/watch-summary")).data;
export const uploadVatRateOverride = async (file) => {
  const form = new FormData();
  form.append("file", file);
  return (await authClient.post("/settings/vat-rates/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  })).data;
};
export const deleteVatRateOverride = async (id) => (await authClient.delete(`/settings/vat-rates/${id}`)).data;
export const listOperators = async () => (await authClient.get("/operators")).data;
// roadmap 6.1 follow-up (operator 2026-10-18): the backend takes
// {email, display_name, initial_password, role} and removes by the account id —
// the tab sent {name, email} and an email, so adding and removing never worked.
export const addOperator = async (op) => (await authClient.post("/operators", op)).data;
export const removeOperator = async (id) => (await authClient.delete(`/operators/${encodeURIComponent(id)}`)).data;
export const setOperatorRole = async (id, role) => (await authClient.patch(`/operators/${encodeURIComponent(id)}/role`, { role })).data;
export const setOperatorName = async (id, display_name) => (await authClient.patch(`/operators/${encodeURIComponent(id)}/name`, { display_name })).data;
export const updateMyName = async (display_name) => (await authClient.patch("/operators/me", { display_name })).data;
export const getAudit = async () => (await authClient.get("/audit")).data;
export const resetWorkspace = async (confirm) => (await authClient.post("/admin/reset-workspace", { confirm })).data;
export const resetWorkspacePreview = async () => (await authClient.get("/admin/reset-workspace/preview")).data;
export const resetWorkspaceReceipts = async () => (await authClient.get("/admin/reset-workspace/receipts")).data;
export const listRateVersions = async (regime) => (await authClient.get(`/rates/${regime}/versions`)).data;
